import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { preferencesAPI } from '../services/api';
import { useAuth } from './AuthContext';

type Theme = 'light' | 'dark';
type ThemeMode = 'auto' | 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  themeMode: ThemeMode;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  
  // Función para obtener el tema del sistema
  const getSystemTheme = (): Theme => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  };

  // Estado para el modo de tema (auto, light, dark)
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    // Primero intentar cargar desde localStorage para carga inmediata
    const savedMode = localStorage.getItem('themeMode') as ThemeMode;
    
    // Migración: si no hay themeMode pero hay theme antiguo, migrar
    if (!savedMode) {
      const oldTheme = localStorage.getItem('theme') as Theme;
      if (oldTheme === 'light' || oldTheme === 'dark') {
        return oldTheme;
      }
      return 'auto';
    }
    
    return savedMode;
  });

  // Estado para el tema actual (siempre light o dark)
  const [theme, setThemeState] = useState<Theme>(() => {
    const savedMode = localStorage.getItem('themeMode') as ThemeMode;
    
    if (savedMode === 'light' || savedMode === 'dark') {
      return savedMode;
    }
    
    return getSystemTheme();
  });

  // Cargar preferencias desde la API cuando el usuario esté autenticado
  useEffect(() => {
    const loadThemeFromAPI = async () => {
      // Solo intentar si hay usuario autenticado
      if (!isAuthenticated || !user) {
        console.log('ThemeContext: Usuario no autenticado, usando localStorage');
        return;
      }
      
      try {
        console.log('ThemeContext: Cargando tema desde servidor...');
        const response = await preferencesAPI.get('themeMode');
        const serverThemeMode = response.data.data.value as ThemeMode;
        
        if (serverThemeMode && serverThemeMode !== themeMode) {
          console.log('ThemeContext: Aplicando tema del servidor:', serverThemeMode);
          setThemeModeState(serverThemeMode);
          localStorage.setItem('themeMode', serverThemeMode);
          
          if (serverThemeMode === 'auto') {
            const systemTheme = getSystemTheme();
            setThemeState(systemTheme);
          } else {
            setThemeState(serverThemeMode);
          }
        }
      } catch (error) {
        // Si hay error 404, la preferencia no existe en servidor, mantener localStorage
        if ((error as any)?.response?.status === 404) {
          console.log('ThemeContext: Preferencia no existe en servidor, migrando...');
          // Migrar preferencia actual al servidor
          try {
            await preferencesAPI.set('themeMode', themeMode);
            console.log('ThemeContext: Preferencia migrada al servidor');
          } catch (setError) {
            console.warn('ThemeContext: No se pudo guardar preferencia de tema en servidor:', setError);
          }
        } else {
          console.warn('ThemeContext: Error cargando tema desde servidor:', error);
        }
      }
    };

    // Delay pequeño para asegurar que el usuario esté completamente cargado
    const timer = setTimeout(loadThemeFromAPI, 100);
    return () => clearTimeout(timer);
  }, [isAuthenticated, user, themeMode]);

  const setThemeMode = async (mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem('themeMode', mode);
    
    // Guardar en servidor si el usuario está autenticado
    if (isAuthenticated && user) {
      try {
        await preferencesAPI.set('themeMode', mode);
      } catch (error) {
        console.warn('No se pudo guardar preferencia de tema en servidor:', error);
        // No bloquear el cambio de tema si falla el servidor
      }
    }
    
    if (mode === 'auto') {
      const systemTheme = getSystemTheme();
      setThemeState(systemTheme);
      document.documentElement.setAttribute('data-theme', systemTheme);
    } else {
      setThemeState(mode);
      document.documentElement.setAttribute('data-theme', mode);
    }
  };

  const setTheme = (newTheme: Theme) => {
    setThemeMode(newTheme);
  };

  const toggleTheme = () => {
    if (themeMode === 'auto') {
      // Si está en auto, cambiar a manual según el tema actual
      const newTheme = theme === 'light' ? 'dark' : 'light';
      setThemeMode(newTheme);
    } else {
      // Si está en manual, alternar
      const newTheme = theme === 'light' ? 'dark' : 'light';
      setThemeMode(newTheme);
    }
  };

  // Aplicar tema inicial
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Escuchar cambios en la preferencia del sistema
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (event: MediaQueryListEvent) => {
      // Solo cambiar automáticamente si está en modo 'auto'
      if (themeMode === 'auto') {
        const newTheme = event.matches ? 'dark' : 'light';
        setThemeState(newTheme);
        document.documentElement.setAttribute('data-theme', newTheme);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  const value: ThemeContextType = {
    theme,
    themeMode,
    toggleTheme,
    setTheme,
    setThemeMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};